import { useState } from "react";
import { Award, Download, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/i18n";

const roundRect = (ctx, x, y, w, h, r) => { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); };

export const drawDiploma = (name, stars, lang = "es") => {
  const W = 1600, H = 1131;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#F7F5F0"; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "#2E5A3B"; ctx.lineWidth = 14; roundRect(ctx, 40, 40, W - 80, H - 80, 28); ctx.stroke();
  ctx.strokeStyle = "#C86A4B"; ctx.lineWidth = 4; roundRect(ctx, 70, 70, W - 140, H - 140, 20); ctx.stroke();
  const pins = ["#F2AEC4", "#C6AE4E", "#BCE3F2"];
  for (let i = 0; i < 26; i++) { ctx.fillStyle = pins[i % 3]; ctx.beginPath(); ctx.arc(120 + i * 54, 118, 9, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.arc(120 + i * 54, H - 118, 9, 0, Math.PI * 2); ctx.fill(); }
  ctx.textAlign = "center";
  ctx.fillStyle = "#C86A4B"; ctx.font = "bold 26px Arial, sans-serif";
  ctx.fillText(lang === "es" ? "PAME3D · MISS PAM TRANSFORMA · BARRANQUILLA" : "PAME3D · MISS PAM TRANSFORMA · BARRANQUILLA", W / 2, 200);
  ctx.fillStyle = "#132A1D"; ctx.font = "bold 92px Georgia, serif";
  ctx.fillText(lang === "es" ? "Diploma de Constructor" : "Builder Diploma", W / 2, 320);
  ctx.fillStyle = "#5C5C5C"; ctx.font = "34px Arial, sans-serif";
  ctx.fillText(lang === "es" ? "se otorga con orgullo a" : "is proudly awarded to", W / 2, 400);
  ctx.fillStyle = "#2E5A3B"; ctx.font = "bold 120px Georgia, serif";
  ctx.fillText(name, W / 2, 545);
  ctx.strokeStyle = "#C6AE4E"; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(W / 2 - 420, 580); ctx.lineTo(W / 2 + 420, 580); ctx.stroke();
  ctx.fillStyle = "#132A1D"; ctx.font = "36px Arial, sans-serif";
  ctx.fillText(lang === "es" ? "por completar los 12 retos del Tablero de Construcción Pame3D" : "for completing the 12 challenges of the Pame3D Building Board", W / 2, 660);
  ctx.fillText(lang === "es" ? `con ${stars} de 36 estrellas` : `with ${stars} of 36 stars`, W / 2, 715);
  const sx = W / 2 - 3 * 70;
  for (let i = 0; i < 6; i++) {
    ctx.fillStyle = i < Math.round((stars / 36) * 6) ? "#E8A712" : "#E5DED0";
    ctx.beginPath();
    for (let k = 0; k < 10; k++) { const r = k % 2 ? 16 : 36, a = (Math.PI / 5) * k - Math.PI / 2; ctx.lineTo(sx + i * 140 + 70 + Math.cos(a) * r, 810 + Math.sin(a) * r); }
    ctx.closePath(); ctx.fill();
  }
  ctx.fillStyle = "#5C5C5C"; ctx.font = "28px Arial, sans-serif";
  ctx.fillText(new Date().toLocaleDateString(lang === "es" ? "es-CO" : "en-US", { day: "numeric", month: "long", year: "numeric" }), W / 2, 930);
  ctx.font = "italic 30px Georgia, serif"; ctx.fillStyle = "#2E5A3B";
  ctx.fillText(lang === "es" ? "Cada producto nace de una botella · pame3d.com/retos" : "Every product is born from a bottle · pame3d.com/retos", W / 2, 990);
  return c;
};

export const Diploma = ({ stars, unlocked }) => {
  const { t, lang } = useLang();
  const d = t.diploma;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(localStorage.getItem("pame3d_kid") || "");
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  const make = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      localStorage.setItem("pame3d_kid", name.trim());
      setUrl(drawDiploma(name.trim(), stars, lang).toDataURL("image/png"));
    } catch { toast.error(d.error); }
    setBusy(false);
  };
  const download = () => { const a = document.createElement("a"); a.href = url; a.download = `diploma-pame3d-${name.trim().toLowerCase().replace(/\s+/g, "-")}.png`; a.click(); };
  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error(d.popup);
    w.document.write(`<html><head><title>Diploma Pame3D</title><style>@page{size:landscape;margin:0}body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh}img{width:100%;max-height:100vh;object-fit:contain}</style></head><body><img src="${url}" onload="setTimeout(()=>{window.print()},300)"></body></html>`);
    w.document.close();
  };

  return (
    <>
      <button data-testid="diploma-btn" onClick={() => { setUrl(null); setOpen(true); }} disabled={!unlocked} title={unlocked ? d.ready : d.locked}
        className="inline-flex items-center gap-2 rounded-full bg-[#E8A712] text-[#132A1D] px-5 py-2.5 text-sm font-semibold hover:bg-[#C86A4B] hover:text-white transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed">
        <Award size={16} /> {d.button}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-[#F7F5F0]" data-testid="diploma-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-d text-2xl text-[#132A1D]">{d.title}</DialogTitle>
            <DialogDescription className="text-[#5C5C5C]">{url ? d.doneSub : d.sub}</DialogDescription>
          </DialogHeader>
          {url ? (
            <div className="space-y-4">
              <img src={url} alt={d.title} data-testid="diploma-preview" className="w-full rounded-xl border border-black/10 shadow-md" />
              <div className="flex flex-wrap gap-2">
                <button data-testid="diploma-download" onClick={download} className="inline-flex items-center gap-2 rounded-full bg-[#2E5A3B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C86A4B] transition-colors duration-200"><Download size={15} /> {d.download}</button>
                <button data-testid="diploma-print" onClick={print} className="inline-flex items-center gap-2 rounded-full border border-black/15 text-[#132A1D] px-5 py-2.5 text-sm font-semibold hover:bg-white transition-colors duration-200"><Printer size={15} /> {d.print}</button>
                <button data-testid="diploma-back" onClick={() => setUrl(null)} className="text-sm text-[#5C5C5C] underline underline-offset-4 px-2">{d.changeName}</button>
              </div>
            </div>
          ) : (
            <form onSubmit={make} className="space-y-3">
              <input data-testid="diploma-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={30} placeholder={d.namePh} required
                className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-[#2E5A3B]" />
              <button type="submit" data-testid="diploma-submit" disabled={busy || !name.trim()}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2E5A3B] text-white px-6 py-3 text-sm font-semibold hover:bg-[#C86A4B] transition-colors duration-200 disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Award size={16} />} {d.submit}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
